// src/mockData/customers.ts

// Base customer template
const createCustomer = (
  id: string,
  name: string,
  assignedTo: string,
  amounts: {
    current030: number;
    over3160: number;
    over6190: number;
    over91120: number;
    over121180: number;
  }
) => {
  const total = Object.values(amounts).reduce((sum, amt) => sum + amt, 0);
  const over90 = amounts.over91120 + amounts.over121180;
  const current090 = amounts.current030 + amounts.over3160 + amounts.over6190;
  
  return {
    id,
    name,
    accountNumber: `ACC${Math.floor(Math.random() * 90000) + 10000}`,
    assignedTo,
    current030: amounts.current030,
    over3160: amounts.over3160,
    over6190: amounts.over6190,
    over91120: amounts.over91120,
    over121180: amounts.over121180,
    totalOutstanding: total,
    current090,
    over90,
    over180: amounts.over121180,
    over365: 0,
    creditScore: Math.floor(Math.random() * 40) + 60,
    riskLevel: over90 > 50000 ? 'high' as const : over90 > 20000 ? 'medium' as const : 'low' as const,
    inActiveCollection: over90 > 0,
    collectionAmount: over90
  };
};

// Sarah Johnson's customers (user-001) - 3 accounts in collection queue
const sarahCustomers = [
  createCustomer('CUST-001', 'Tech Solutions Inc', 'user-001', {
    current030: 450000,
    over3160: 280000,
    over6190: 120000,
    over91120: 340000,
    over121180: 550000
  }),
  createCustomer('CUST-002', 'Global Industries Ltd', 'user-001', {
    current030: 750000,
    over3160: 300000,
    over6190: 200000,
    over91120: 450000,
    over121180: 380000
  }),
  createCustomer('CUST-003', 'Manufacturing Co', 'user-001', {
    current030: 2000000,
    over3160: 1500000,
    over6190: 100000,
    over91120: 750000,
    over121180: 420000
  }),
  // Additional Sarah customers (not in high-risk queue)
  createCustomer('CUST-004', 'Retail Partners LLC', 'user-001', {
    current030: 125000,
    over3160: 85000,
    over6190: 40000,
    over91120: 15000,
    over121180: 8330
  }),
  createCustomer('CUST-005', 'Service Dynamics', 'user-001', {
    current030: 95000,
    over3160: 62000,
    over6190: 28000,
    over91120: 0,
    over121180: 0
  })
];

// Mike Chen's customers (user-002)
const mikeCustomers = [
  createCustomer('CUST-006', 'Acme Corporation', 'user-002', {
    current030: 500000,
    over3160: 750000,
    over6190: 300000,
    over91120: 250000,
    over121180: 250000
  }),
  createCustomer('CUST-007', 'BuildRight Construction', 'user-002', {
    current030: 680000,
    over3160: 420000,
    over6190: 180000,
    over91120: 320000,
    over121180: 180000
  }),
  createCustomer('CUST-008', 'Metro Logistics', 'user-002', {
    current030: 890000,
    over3160: 560000,
    over6190: 240000,
    over91120: 410000,
    over121180: 290000
  }),
  createCustomer('CUST-009', 'TechFlow Systems', 'user-002', {
    current030: 340000,
    over3160: 210000,
    over6190: 95000,
    over91120: 180000,
    over121180: 120000
  }),
  createCustomer('CUST-010', 'Premier Supplies', 'user-002', {
    current030: 275000,
    over3160: 165000,
    over6190: 78000,
    over91120: 0,
    over121180: 0
  })
];

// Emily Rodriguez's customers (user-003)
const emilyCustomers = [
  createCustomer('CUST-011', 'DataCore Solutions', 'user-003', {
    current030: 920000,
    over3160: 680000,
    over6190: 340000,
    over91120: 480000,
    over121180: 360000
  }),
  createCustomer('CUST-012', 'Industrial Partners', 'user-003', {
    current030: 1100000,
    over3160: 850000,
    over6190: 420000,
    over91120: 560000,
    over121180: 420000
  }),
  createCustomer('CUST-013', 'Quantum Enterprises', 'user-003', {
    current030: 780000,
    over3160: 520000,
    over6190: 260000,
    over91120: 340000,
    over121180: 240000
  }),
  createCustomer('CUST-014', 'Horizon Trading', 'user-003', {
    current030: 450000,
    over3160: 290000,
    over6190: 140000,
    over91120: 210000,
    over121180: 150000
  }),
  createCustomer('CUST-015', 'Velocity Systems', 'user-003', {
    current030: 320000,
    over3160: 195000,
    over6190: 88000,
    over91120: 0,
    over121180: 0
  })
];

// David Kim's customers (user-004)
const davidCustomers = [
  createCustomer('CUST-016', 'Nexus Corporation', 'user-004', {
    current030: 1250000,
    over3160: 940000,
    over6190: 480000,
    over91120: 620000,
    over121180: 480000
  }),
  createCustomer('CUST-017', 'Precision Manufacturing', 'user-004', {
    current030: 890000,
    over3160: 650000,
    over6190: 320000,
    over91120: 440000,
    over121180: 320000
  }),
  createCustomer('CUST-018', 'Summit Distributors', 'user-004', {
    current030: 670000,
    over3160: 480000,
    over6190: 230000,
    over91120: 310000,
    over121180: 220000
  }),
  createCustomer('CUST-019', 'Apex Solutions', 'user-004', {
    current030: 540000,
    over3160: 380000,
    over6190: 180000,
    over91120: 240000,
    over121180: 170000
  }),
  createCustomer('CUST-020', 'CoreTech Industries', 'user-004', {
    current030: 410000,
    over3160: 280000,
    over6190: 130000,
    over91120: 0,
    over121180: 0
  })
];

// Lisa Anderson's customers (user-005)
const lisaCustomers = [
  createCustomer('CUST-021', 'Titan Enterprises', 'user-005', {
    current030: 1450000,
    over3160: 1100000,
    over6190: 560000,
    over91120: 720000,
    over121180: 560000
  }),
  createCustomer('CUST-022', 'Omega Systems', 'user-005', {
    current030: 980000,
    over3160: 720000,
    over6190: 360000,
    over91120: 480000,
    over121180: 360000
  }),
  createCustomer('CUST-023', 'Fusion Technologies', 'user-005', {
    current030: 760000,
    over3160: 540000,
    over6190: 270000,
    over91120: 360000,
    over121180: 260000
  }),
  createCustomer('CUST-024', 'Catalyst Group', 'user-005', {
    current030: 620000,
    over3160: 440000,
    over6190: 210000,
    over91120: 280000,
    over121180: 200000
  }),
  createCustomer('CUST-025', 'Pinnacle Corp', 'user-005', {
    current030: 480000,
    over3160: 330000,
    over6190: 150000,
    over91120: 0,
    over121180: 0
  })
];

// James Wilson's customers (user-006)
const jamesCustomers = [
  createCustomer('CUST-026', 'Vanguard Industries', 'user-006', {
    current030: 1680000,
    over3160: 1280000,
    over6190: 640000,
    over91120: 840000,
    over121180: 640000
  }),
  createCustomer('CUST-027', 'Zenith Partners', 'user-006', {
    current030: 1120000,
    over3160: 840000,
    over6190: 420000,
    over91120: 560000,
    over121180: 420000
  }),
  createCustomer('CUST-028', 'Momentum Solutions', 'user-006', {
    current030: 890000,
    over3160: 640000,
    over6190: 320000,
    over91120: 420000,
    over121180: 310000
  }),
  createCustomer('CUST-029', 'Synergy Enterprises', 'user-006', {
    current030: 720000,
    over3160: 520000,
    over6190: 250000,
    over91120: 330000,
    over121180: 240000
  }),
  createCustomer('CUST-030', 'Vertex Corporation', 'user-006', {
    current030: 560000,
    over3160: 390000,
    over6190: 180000,
    over91120: 0,
    over121180: 0
  })
];

// Combine all customers
export const CUSTOMERS = [
  ...sarahCustomers,
  ...mikeCustomers,
  ...emilyCustomers,
  ...davidCustomers,
  ...lisaCustomers,
  ...jamesCustomers
];
